"use client";

import posthog from "posthog-js";

import { type AnalyticsApiEventName } from "@/lib/generated/analytics-api-contract";
import {
  NormalizedPaywallTrigger,
  PaywallTrigger,
  readStoredPaywallContext,
  StoredPaywallContext,
  writeStoredPaywallContext,
} from "@/lib/paywall-context";

type AnalyticsProperties = Record<string, unknown>;

export type AnalyticsSource = "YouTube" | "Instagram" | "TikTok" | "Upload";
export type AnalyticsPlatform = "instagram" | "tiktok" | "other";
export type PreviewFailureReason = "timeout" | "private_account" | "unsupported_url" | "rate_limited" | "unknown";
export type SocialOutputType = "linkedin_post" | "twitter_thread" | "blog_post" | "transcript" | "hashtags" | "summary";
export type SocialAssetType = "video" | "transcript_txt" | "transcript_srt";
export type SocialCopySource = "studio_tab" | "text_tab" | "manual_selection";
export type SocialDownloadSource = "download_button";
export type SocialUpgradeLocation = "social_sidebar" | "social_limit" | "studio_header" | "studio_teaser";
export type StudioTabName = "studio" | "text" | "captions" | "ai_tools";
export type StudioInteractionType = "click" | "open" | "close" | "select" | "play" | "submit" | "copy" | "download";
export type { PaywallTrigger, StoredPaywallContext } from "@/lib/paywall-context";
export { clearStoredPaywallContext } from "@/lib/paywall-context";

type TranscriptionSubmissionRecord = {
  submission_id: string;
  started_at_ms: number;
  platform: AnalyticsPlatform;
  job_id?: string | null;
  uuid_filename?: string | null;
};

type AnalyticsEventName = AnalyticsApiEventName | "landing_unsupported_link_submitted" | "social_output_exposed" | "studio_interaction";

const TRANSCRIPTION_SUBMISSIONS_KEY = "_social_transcription_submissions";
const MAX_STORED_TRANSCRIPTION_SUBMISSIONS = 25;

function _canTrack() {
  return typeof window !== "undefined" && Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
}

function _safeSessionStorageGet(key: string) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function _safeSessionStorageSet(key: string, value: string) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(key, value);
  } catch {}
}

function _createId(prefix: string) {
  const randomPart =
    typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return `${prefix}_${randomPart}`;
}

function _withoutUndefined(properties: AnalyticsProperties) {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined));
}

function _readTranscriptionSubmissions(): TranscriptionSubmissionRecord[] {
  const raw = _safeSessionStorageGet(TRANSCRIPTION_SUBMISSIONS_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as TranscriptionSubmissionRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function _writeTranscriptionSubmissions(records: TranscriptionSubmissionRecord[]) {
  _safeSessionStorageSet(TRANSCRIPTION_SUBMISSIONS_KEY, JSON.stringify(records.slice(-MAX_STORED_TRANSCRIPTION_SUBMISSIONS)));
}

function _getPlatformFromSource(source: AnalyticsSource): AnalyticsPlatform {
  if (source === "Instagram") {
    return "instagram";
  }
  if (source === "TikTok") {
    return "tiktok";
  }
  return "other";
}

function _normalizePaywallTrigger(trigger: PaywallTrigger): NormalizedPaywallTrigger {
  if (trigger === "daily_limit_reached") {
    return "daily_limit";
  }
  if (trigger === "premium_feature_clicked") {
    return "feature_gate";
  }
  return "manual";
}

export function identifyAnalyticsUser(userId: string, properties?: AnalyticsProperties) {
  if (!_canTrack() || !userId) {
    return;
  }

  posthog.identify(userId, properties);
}

export function resetAnalyticsUser() {
  if (!_canTrack()) {
    return;
  }

  posthog.reset();
}

function _sendEvent(eventName: AnalyticsEventName, properties?: AnalyticsProperties, userId?: string) {
  if (!_canTrack()) {
    return;
  }

  if (userId) {
    posthog.identify(userId);
  }

  try {
    posthog.capture(eventName, properties ?? {});
  } catch (error) {
    console.warn("analytics event failed", eventName, error);
  }
}

export function getSourceFromUrl(value: string): AnalyticsSource {
  const host = getDomainFromUrl(value);
  if (!host) {
    return "Upload";
  }

  if (host.includes("youtube.com") || host.includes("youtu.be")) {
    return "YouTube";
  }
  if (host.includes("instagram.com")) {
    return "Instagram";
  }
  if (host.includes("tiktok.com")) {
    return "TikTok";
  }

  return "Upload";
}

export function getSourceFromFileSource(source?: string | null): AnalyticsSource {
  if (!source) {
    return "Upload";
  }

  const normalized = source.toLowerCase();
  if (normalized.includes("youtube")) return "YouTube";
  if (normalized.includes("instagram")) return "Instagram";
  if (normalized.includes("tiktok")) return "TikTok";
  return "Upload";
}

export function getPlatformFromUrl(value: string): AnalyticsPlatform {
  return _getPlatformFromSource(getSourceFromUrl(value));
}

export function getDomainFromUrl(value: string): string | null {
  const input = value.trim();
  if (!input) {
    return null;
  }

  const normalized = /^https?:\/\//i.test(input) ? input : `https://${input}`;

  try {
    const hostname = new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
    return hostname || null;
  } catch {
    return null;
  }
}

export function getPlatformFromFileSource(source?: string | null): AnalyticsPlatform {
  return _getPlatformFromSource(getSourceFromFileSource(source));
}

export function classifyPreviewFailureReason(params: {
  status?: number;
  message?: string | null;
  reason?: string | null;
}): PreviewFailureReason {
  if (
    params.reason === "timeout" ||
    params.reason === "private_account" ||
    params.reason === "unsupported_url" ||
    params.reason === "rate_limited" ||
    params.reason === "unknown"
  ) {
    return params.reason;
  }

  if (params.status === 429) {
    return "rate_limited";
  }

  const normalizedMessage = (params.message ?? "").toLowerCase();
  if (normalizedMessage.includes("private")) {
    return "private_account";
  }
  if (
    normalizedMessage.includes("unsupported") ||
    normalizedMessage.includes("invalid") ||
    normalizedMessage.includes("only instagram reel") ||
    normalizedMessage.includes("only tiktok") ||
    normalizedMessage.includes("video url not found") ||
    normalizedMessage.includes("video id not found")
  ) {
    return "unsupported_url";
  }
  if (
    normalizedMessage.includes("timeout") ||
    normalizedMessage.includes("couldn't reach") ||
    normalizedMessage.includes("could not reach") ||
    normalizedMessage.includes("request failed") ||
    normalizedMessage.includes("network")
  ) {
    return "timeout";
  }

  return "unknown";
}

export function buildUseCase(source: AnalyticsSource, outputType: string) {
  return `${source} -> ${outputType}`;
}

export function trackLinkPasted(source: AnalyticsSource, properties?: AnalyticsProperties) {
  void _sendEvent(
    "link_pasted",
    _withoutUndefined({
      source,
      platform: _getPlatformFromSource(source),
      ...(properties ?? {}),
    })
  );
}

export function trackLandingUnsupportedLinkSubmitted(url: string, properties?: AnalyticsProperties) {
  void _sendEvent(
    "landing_unsupported_link_submitted",
    _withoutUndefined({
      submitted_domain: getDomainFromUrl(url) ?? undefined,
      surface: "landing_hero",
      ...(properties ?? {}),
    })
  );
}

export function trackPremiumFeatureClicked(featureName: string) {
  void _sendEvent("feature_used", { feature: featureName });
}

export function trackSocialWorkspaceOpened(properties?: AnalyticsProperties) {
  void _sendEvent("social_workspace_opened", properties);
}

export function trackSocialStudioOpened(properties?: AnalyticsProperties) {
  void _sendEvent("social_studio_opened", properties);
}

export function trackSocialPreviewLoaded(properties?: AnalyticsProperties) {
  void _sendEvent("social_preview_loaded", properties);
}

export function trackSocialOutputExposed(outputType: SocialOutputType, properties?: AnalyticsProperties) {
  void _sendEvent("social_output_exposed", { output_type: outputType, format: outputType, ...(properties ?? {}) });
}

export function trackSocialOutputCopied(outputType: SocialOutputType, properties?: AnalyticsProperties) {
  void _sendEvent(
    "social_output_copied",
    _withoutUndefined({
      output_type: outputType,
      format: outputType,
      format_at_time_of_copy: outputType,
      ...(properties ?? {}),
    })
  );
}

export function trackTranscriptCopied(properties?: AnalyticsProperties, userId?: string) {
  void _sendEvent("transcript_copied", _withoutUndefined(properties ?? {}), userId);
}

export function trackSocialAssetDownloaded(assetType: SocialAssetType, properties?: AnalyticsProperties) {
  void _sendEvent("social_asset_downloaded", { asset_type: assetType, ...(properties ?? {}) });
}

export function trackSocialUpgradeClicked(location: SocialUpgradeLocation, properties?: AnalyticsProperties) {
  void _sendEvent("social_upgrade_clicked", { location, ...(properties ?? {}) });
}

export function trackTranscriptionStarted(properties?: AnalyticsProperties) {
  const submissionId = _createId("submission");
  const record: TranscriptionSubmissionRecord = {
    submission_id: submissionId,
    started_at_ms: Date.now(),
    platform: (properties?.platform as AnalyticsPlatform | undefined) ?? "other",
  };
  const storedRecords = _readTranscriptionSubmissions();
  storedRecords.push(record);
  _writeTranscriptionSubmissions(storedRecords);

  void _sendEvent(
    "transcription_started",
    _withoutUndefined({
      submission_id: submissionId,
      ...(properties ?? {}),
    })
  );

  return submissionId;
}

export function linkTrackedTranscriptionSubmission(
  submissionId: string,
  identifiers: { job_id?: string | null; uuid_filename?: string | null }
) {
  const storedRecords = _readTranscriptionSubmissions();
  const nextRecords = storedRecords.map(record =>
    record.submission_id === submissionId
      ? {
          ...record,
          job_id: identifiers.job_id ?? record.job_id,
          uuid_filename: identifiers.uuid_filename ?? record.uuid_filename,
        }
      : record
  );
  _writeTranscriptionSubmissions(nextRecords);
}

export function findTrackedTranscriptionSubmission(match: { job_id?: string | null; uuid_filename?: string | null }) {
  const storedRecords = _readTranscriptionSubmissions();
  return (
    storedRecords.find(record => record.job_id && match.job_id && record.job_id === match.job_id) ??
    storedRecords.find(record => record.uuid_filename && match.uuid_filename && record.uuid_filename === match.uuid_filename) ??
    null
  );
}

export function trackStudioReady(properties?: AnalyticsProperties) {
  void _sendEvent("studio_ready", properties);
}

export function trackOutputFormatSelected(format: SocialOutputType, properties?: AnalyticsProperties) {
  void _sendEvent("output_format_selected", { format, ...(properties ?? {}) });
}

export function trackStudioTabViewed(tab: StudioTabName, properties?: AnalyticsProperties) {
  void _sendEvent("studio_tab_viewed", { tab, ...(properties ?? {}) });
}

export function trackStudioInteraction(properties?: AnalyticsProperties) {
  void _sendEvent("studio_interaction", _withoutUndefined(properties ?? {}));
}

export function trackPaywallHit(
  trigger: PaywallTrigger,
  options?: {
    feature?: string;
    links_used_today?: number;
    links_remaining?: number;
    user_id?: string;
  }
) {
  const context: StoredPaywallContext = {
    paywall_impression_id: _createId("paywall"),
    trigger_type: trigger,
    trigger: _normalizePaywallTrigger(trigger),
    feature: options?.feature ?? null,
    links_used_today: options?.links_used_today ?? null,
    links_remaining: options?.links_remaining ?? null,
    plan_viewed_at: null,
  };

  writeStoredPaywallContext(context);
  void _sendEvent(
    "paywall_shown",
    _withoutUndefined({
      paywall_impression_id: context.paywall_impression_id,
      trigger_type: context.trigger_type,
      trigger: context.trigger,
      feature: context.feature,
      links_used_today: context.links_used_today,
      links_remaining: context.links_remaining,
    }),
    options?.user_id
  );

  return context;
}

export function trackPaywallDismissed(context: StoredPaywallContext | null, properties?: AnalyticsProperties, userId?: string) {
  if (!context) {
    return;
  }

  void _sendEvent(
    "paywall_dismissed",
    _withoutUndefined({
      paywall_impression_id: context.paywall_impression_id,
      trigger_type: context.trigger_type,
      trigger: context.trigger,
      feature: context.feature,
      links_used_today: context.links_used_today,
      links_remaining: context.links_remaining,
      ...(properties ?? {}),
    }),
    userId
  );
}

export function trackPaywallPlanViewed(userId?: string) {
  const context = readStoredPaywallContext();
  if (!context || context.plan_viewed_at) {
    return context;
  }

  const nextContext: StoredPaywallContext = {
    ...context,
    plan_viewed_at: new Date().toISOString(),
  };
  writeStoredPaywallContext(nextContext);

  void _sendEvent(
    "paywall_plan_viewed",
    _withoutUndefined({
      paywall_impression_id: nextContext.paywall_impression_id,
      trigger_type: nextContext.trigger_type,
      trigger: nextContext.trigger,
      feature: nextContext.feature,
      links_used_today: nextContext.links_used_today,
      links_remaining: nextContext.links_remaining,
    }),
    userId
  );

  return nextContext;
}

export function trackPaywallUpgradeClicked(properties?: AnalyticsProperties, userId?: string) {
  const context = readStoredPaywallContext();
  const nextContext = context
    ? {
        ...context,
        upgrade_clicked_at: new Date().toISOString(),
        upgrade_entry_point: typeof properties?.entry_point === "string" ? properties.entry_point : (context.upgrade_entry_point ?? null),
      }
    : null;

  if (nextContext) {
    writeStoredPaywallContext(nextContext);
  }

  void _sendEvent(
    "upgrade_clicked",
    _withoutUndefined({
      paywall_impression_id: nextContext?.paywall_impression_id ?? context?.paywall_impression_id ?? null,
      trigger_type: nextContext?.trigger_type ?? context?.trigger_type ?? null,
      trigger: nextContext?.trigger ?? context?.trigger ?? null,
      feature: nextContext?.feature ?? context?.feature ?? null,
      links_used_today: nextContext?.links_used_today ?? context?.links_used_today ?? null,
      links_remaining: nextContext?.links_remaining ?? context?.links_remaining ?? null,
      plan_viewed_at: nextContext?.plan_viewed_at ?? context?.plan_viewed_at ?? null,
      upgrade_clicked_at: nextContext?.upgrade_clicked_at ?? null,
      upgrade_entry_point: nextContext?.upgrade_entry_point ?? null,
      ...(properties ?? {}),
    }),
    userId
  );
}

export function getStoredPaywallContext() {
  return readStoredPaywallContext();
}

export function trackAnalyticsApiEvent(eventName: AnalyticsApiEventName, properties?: AnalyticsProperties, userId?: string) {
  void _sendEvent(eventName, properties, userId);
}
